import React, { useState } from 'react';
import { findCell, findCellByPackage, lengthLabel, pricePerYearLabel } from 'subscriptionOptions/optionsGrid';
import { formatPrice } from 'utils/formating';
import { GridSelection, GridSubject, OptionsGridModel, PackageResult } from 'subscriptionOptions/types';

export type OptionsGridPresentation = 'auto' | 'cards' | 'table';

export type CellPrices = { [packagesId: number]: PackageResult };

type OptionsGridProps = {
  grid: OptionsGridModel;
  selection: GridSelection;
  /** null packagesId deselects the subject. */
  onSelect: (subjectsId: number, packagesId: number | null) => void;
  /** cards = length tiles per subject; table = tier radios and length columns; auto picks by tier count. */
  presentation?: OptionsGridPresentation;
  /** When present, tiles show a price. */
  cellPrices?: CellPrices;
  chargeVat?: boolean;
  hasError?: boolean;
};

/** Selectable grid for an options response. Driven by the grid model, so ADD and RENEW share it. */
export const OptionsGrid: React.FC<OptionsGridProps> = ({
  grid,
  selection,
  onSelect,
  presentation = 'auto',
  cellPrices,
  chargeVat = false,
  hasError = false,
}) => {
  const resolved: OptionsGridPresentation =
    presentation === 'auto' ? (grid.subjects.some((s) => s.bMultipleGeneralPackages) ? 'table' : 'cards') : presentation;

  if (grid.subjects.length === 0) {
    return null;
  }

  if (resolved === 'cards') {
    return (
      <>
        {grid.subjects.map((subject, index) => (
          <SubjectCards
            key={'Subject_Cards_' + subject.subjectsId}
            subject={subject}
            index={index}
            selectedPackageId={selection[subject.subjectsId]}
            onSelect={onSelect}
            cellPrices={cellPrices}
            chargeVat={chargeVat}
          />
        ))}
      </>
    );
  }

  return (
    <div className={'SRPkgSelection' + (hasError ? ' HasError' : '')}>
      <div className="Row HdrRow">
        <div className="EmptyCell"></div>
        {grid.lengths.map((length, idx) => (
          <React.Fragment key={'Hdr_' + length.subscriptionTypesId}>
            <div className="HdrCell">{length.subscriptionTypesName}</div>
            {idx !== grid.lengths.length - 1 && <div className="CellSpacer"></div>}
          </React.Fragment>
        ))}
      </div>
      {grid.subjects.map((subject) => (
        <SubjectRow
          key={'Subject_Row_' + subject.subjectsId}
          subject={subject}
          lengths={grid.lengths}
          selectedPackageId={selection[subject.subjectsId]}
          onSelect={onSelect}
          cellPrices={cellPrices}
          chargeVat={chargeVat}
        />
      ))}
    </div>
  );
};

/* ---------- cards ---------- */

type SubjectCardsProps = {
  subject: GridSubject;
  index: number;
  selectedPackageId: number | undefined;
  onSelect: (subjectsId: number, packagesId: number | null) => void;
  cellPrices?: CellPrices;
  chargeVat: boolean;
};

const SubjectCards: React.FC<SubjectCardsProps> = ({ subject, index, selectedPackageId, onSelect, cellPrices, chargeVat }) => {
  const tierName = subject.tiers.length === 1 ? subject.tiers[0].generalPackagesName + ' ' : '';

  return (
    <div className="SubDetailsBlock">
      <div className="LineSpacer3"></div>
      <h3>
        Your subscription options for {tierName}
        {subject.subjectsName} are:
      </h3>
      <div className="LineSpacer3"></div>

      <div className="SubSelectOptRow">
        {subject.lengths.map((length) => {
          const cell = subject.cells.find((c) => c.subscriptionTypesId === length.subscriptionTypesId);

          if (!cell) {
            return null;
          }

          const selected = selectedPackageId === cell.packagesId;
          const price = cellPrices ? cellPrices[cell.packagesId] : undefined;

          return (
            <label className={'SubOption' + (selected ? ' Checked' : '')} key={'SubOption_' + cell.packagesId}>
              <input
                type="radio"
                name={'subLengthOpts_' + index}
                id={'subLengthOpts_' + cell.packagesId}
                className="HiddenInput"
                checked={selected}
                onChange={() => onSelect(subject.subjectsId, cell.packagesId)}
              />
              <label className="CustomInput" htmlFor={'subLengthOpts_' + cell.packagesId}>
                {lengthLabel(cell)}
                <span>
                  {price ? priceLabel(price.netUndiscounted, chargeVat) : ''}
                  {price && cell.package.subscriptionTypesDuration > 1 && (
                    <span className="SmTxt">{pricePerYearLabel(price.netUndiscounted, cell.package.subscriptionTypesDuration)}</span>
                  )}
                </span>
              </label>
            </label>
          );
        })}
      </div>
    </div>
  );
};

/* ---------- table ---------- */

type SubjectRowProps = {
  subject: GridSubject;
  lengths: OptionsGridModel['lengths'];
  selectedPackageId: number | undefined;
  onSelect: (subjectsId: number, packagesId: number | null) => void;
  cellPrices?: CellPrices;
  chargeVat: boolean;
};

const SubjectRow: React.FC<SubjectRowProps> = ({ subject, lengths, selectedPackageId, onSelect, cellPrices, chargeVat }) => {
  const selectedCell = selectedPackageId !== undefined ? findCellByPackage(subject, selectedPackageId) : undefined;
  // Tier follows the selection; state only holds a tier picked while nothing is selected.
  const [pendingTierId, setPendingTierId] = useState<number | null>(null);
  const tierId = selectedCell ? selectedCell.generalPackagesId : pendingTierId ?? subject.tiers[0].generalPackagesId;

  const changeTier = (generalPackagesId: number) => {
    if (generalPackagesId === tierId) {
      return;
    }

    setPendingTierId(generalPackagesId);

    if (selectedCell) {
      // Carry the chosen length into the new tier, dropping the selection only if it has no such package.
      const sameLength = findCell(subject, generalPackagesId, selectedCell.subscriptionTypesId);
      onSelect(subject.subjectsId, sameLength ? sameLength.packagesId : null);
    }
  };

  return (
    <div className="Row">
      <div className="SubDetailsCell">
        <div className="SDInner">
          <h2>{subject.subjectsName}</h2>

          {subject.bMultipleGeneralPackages && (
            <div className="ClassOptList">
              {subject.tiers.map((tier) => (
                <div className="CLOpt" key={subject.subjectsId + '_' + tier.generalPackagesId}>
                  <div className="Checkbox">
                    <input
                      id={subject.subjectsId + '_' + tier.generalPackagesId}
                      type="radio"
                      name={subject.subjectsId + '_Class_List'}
                      checked={tier.generalPackagesId === tierId}
                      onChange={() => changeTier(tier.generalPackagesId)}
                    />
                    <label className="CustomInput" htmlFor={subject.subjectsId + '_' + tier.generalPackagesId}>
                      {tier.generalPackagesName}
                    </label>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {lengths.map((length, idx) => {
        const cell = findCell(subject, tierId, length.subscriptionTypesId);
        const selected = !!cell && selectedPackageId === cell.packagesId;
        const price = cell && cellPrices ? cellPrices[cell.packagesId] : undefined;

        return (
          <React.Fragment key={'Cell_' + subject.subjectsId + '_' + length.subscriptionTypesId}>
            <div className="PkgSelectCell">
              {cell && (
                <div
                  className={'PkgSCBlock' + (selected ? ' Selected' : '')}
                  onClick={() => onSelect(subject.subjectsId, selected ? null : cell.packagesId)}
                >
                  <div className="Checkbox">
                    <input id={`${cell.packagesId}`} type="radio" name={`${cell.packagesId}`} checked={selected} onChange={() => {}} />
                    <label className="CustomInput" htmlFor={`${cell.packagesId}`} />
                  </div>
                  {price && <span className="CellPrice">{priceLabel(price.netUndiscounted, chargeVat)}</span>}
                </div>
              )}
            </div>
            {idx !== lengths.length - 1 && <div className="CellSpacer"></div>}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const priceLabel = (net: number, chargeVat: boolean) => `£${formatPrice(net)}${chargeVat ? ' + VAT' : ''}`;
